import { Heading, Strong } from '@radix-ui/themes';
import { CommonAlert } from 'components/common/alert';
import { CommonFormDialog } from 'components/common/dialog/form-dialog';
import { FormSelect } from 'components/form/select';
import { FormTextField } from 'components/form/textfield';
import { ActionSelect } from 'components/layout/action-select';
import { EB } from 'components/layout/error-boundary';
import { PriceForm } from 'components/main/routes/prices/form';
import { CommonTable } from 'components/table';
import { AuthContext } from 'contexts/auth';
import { DialogType } from 'enums/dialog';
import { DialogState } from 'interfaces/dialog/state';
import { OptionDef } from 'interfaces/options';
import { BaseDoc } from 'lib/interfaces/database/_base';
import { PriceWhole } from 'lib/interfaces/database/price';
import { DateUtil } from 'lib/utilities/date';
import { SortUtil } from 'lib/utilities/sort';
import { StringUtil } from 'lib/utilities/string';
import { useContext, useEffect, useMemo, useState } from 'react';
import usePricesStore from 'stores/prices';
import useSecuritiesStore from 'stores/securities';

export const Prices = () => {
  const { data: securities, load: loadSecurities } = useSecuritiesStore();
  const {
    create,
    data: prices,
    filters,
    load: loadPrices,
    setFilters,
    update,
    delete: deletePrice,
  } = usePricesStore();

  const { perms } = useContext(AuthContext);
  const { securities: crudSecurities, prices: crudPrices } = perms;

  useEffect(() => {
    if (crudSecurities?.read) {
      loadSecurities();
    }
  }, [crudSecurities?.read, loadSecurities]);

  useEffect(() => {
    if (crudPrices?.read) {
      loadPrices();
    }
  }, [crudPrices?.read, loadPrices]);

  const filteredData = useMemo(() => {
    return prices
      .filter(
        (m) =>
          !filters.effective ||
          DateUtil.isSameDay(filters.effective, m.effective)
      )
      .filter(
        (m) => !filters.securityID || filters.securityID === m.securityID
      );
  }, [prices, filters]);

  const [dialog, setDialog] = useState<DialogState>();

  const securityOptions = useMemo<OptionDef[]>(() => {
    return securities.map((o) => ({
      label: o.name,
      value: o.id,
      group: o._issuer?.name,
    }));
  }, [securities]);

  return (
    <EB>
      <Heading>Prices</Heading>
      <CommonTable
        defaultSort={{
          iCol: 2,
          asc: false,
        }}
        toolbarControls={[
          <FormTextField
            label="Effective Date"
            defaultValue={StringUtil.printDate(filters.effective)}
            onOptionalDateChange={(_, vd) =>
              setFilters({
                effective: vd,
              })
            }
            className="width-250px"
            hideLabel
          />,
          <FormSelect
            label="Security"
            defaultValue={filters.securityID}
            options={securityOptions}
            placeholder="Select a Security"
            onOptionalChange={(v) => setFilters({ securityID: v })}
            className="width-250px"
            hideLabel
          />,
        ]}
        toolbarActions={[
          {
            hidden: !crudPrices?.create,
            label: 'Create',
            onClick: () => {
              setDialog({
                key: Date.now(),
                type: DialogType.Create,
                data: {},
              });
            },
          },
        ]}
        columns={[
          {
            header: 'Actions',
            rowFn: (item: PriceWhole) => (
              <ActionSelect
                options={[
                  {
                    hidden: !crudPrices?.update,
                    label: 'Update',
                    value: DialogType.Update,
                  },
                  {
                    hidden: !crudPrices?.delete,
                    label: 'Delete',
                    value: DialogType.Delete,
                  },
                ]}
                onValueChange={(v) => {
                  // new value never gets set but it does trigger the effect below
                  switch (v) {
                    case DialogType.Update:
                    case DialogType.Delete: {
                      setDialog({
                        key: Date.now(),
                        type: v,
                        data: item,
                      });
                      break;
                    }

                    default: {
                      break;
                    }
                  }
                }}
              />
            ),
          },
          {
            header: 'Created',
            rowFn: (item: PriceWhole) =>
              StringUtil.printDateTime(item.createdAt),
            sortFn: (a: PriceWhole, b: PriceWhole) =>
              SortUtil.dates(a.createdAt, b.createdAt),
          },
          {
            header: 'Effective Date',
            rowFn: (item: PriceWhole) => StringUtil.printDate(item.effective),
            sortFn: (a: PriceWhole, b: PriceWhole) =>
              SortUtil.dates(a.effective, b.effective),
          },
          {
            header: 'Security',
            rowFn: (item: PriceWhole) => item._security?.name,
            sortFn: (a: PriceWhole, b: PriceWhole) =>
              SortUtil.dates(a._security?.name, b._security?.name),
          },
          {
            header: 'Price Per Share',
            rowFn: (item: PriceWhole) => StringUtil.printMoney(item.pps),
            sortFn: (a: PriceWhole, b: PriceWhole) =>
              SortUtil.numbers(a.pps, b.pps),
          },
          {
            header: 'Face Value',
            rowFn: (item: PriceWhole) => StringUtil.printMoney(item.face),
            sortFn: (a: PriceWhole, b: PriceWhole) =>
              SortUtil.numbers(a.face, b.face),
          },
          {
            header: 'Par Value',
            rowFn: (item: PriceWhole) => StringUtil.printMoney(item.par),
            sortFn: (a: PriceWhole, b: PriceWhole) =>
              SortUtil.numbers(a.par, b.par),
          },
        ]}
        data={filteredData}
      />

      {dialog?.type === DialogType.Create && (
        <CommonFormDialog
          key={dialog.key}
          title="Create"
          children={<PriceForm />}
          formData={dialog.data}
          submitAction={{
            label: 'Save',
            onClick: (item) => create(item),
          }}
          onClose={() => setDialog(undefined)}
        />
      )}

      {dialog?.type === DialogType.Update && (
        <CommonFormDialog
          key={dialog.key}
          title="Update"
          children={<PriceForm />}
          formData={dialog.data}
          submitAction={{
            label: 'Save',
            onClick: async (item) => {
              return await update((dialog.data as BaseDoc).id, item);
            },
          }}
          onClose={() => setDialog(undefined)}
        />
      )}

      {dialog?.type === DialogType.Delete && (
        <CommonAlert
          key={dialog.key}
          title="Delete"
          description={
            <>
              Are you sure you want to permanently delete this price record?
              <br />
              <Strong>This cannot be undone.</Strong>
            </>
          }
          onConfirm={() => deletePrice((dialog.data as BaseDoc).id)}
        />
      )}
    </EB>
  );
};
